import type { EventDeviceRow, EventRaceSummaryRow } from '../types';
import type { RaceDetailActionGroup } from '../types/RaceDetailPortalTypes';
import {
  formatMoveToStatusLabel,
  isDraftRaceStatus,
  isRaceAssignmentsLocked,
  normalizeRaceStatus,
} from './eventsOverviewHelpers';
import {
  countAssignedRaceDevices,
  countRaceAthleteDevicePairs,
  countRetrievedRaceDevices,
  countRetrievedRaceDevicesByPhase,
  countUnretrievedRaceDevices,
} from './raceRetrievalCounts';
import type { RaceDetailPortalModel } from '../types/RaceDetailPortalTypes';
import type { PackagingToDistributionReadinessCheck } from '../types';
import type { RaceConfigurationRequirements } from '../../../helpers/types/DeviceValidationTypes';

/** Child dialogs opened from the race portal; used to keep the portal open and handle Escape/backdrop. */
export const RACE_STACKED_OVERLAY_SELECTOR = '[data-race-stacked-overlay]';

export function isRaceStackedOverlayOpen(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(RACE_STACKED_OVERLAY_SELECTOR);
}

/** Ignore race portal backdrop clicks briefly after a stacked child dialog unmounts (click-through). */
const RACE_PORTAL_BACKDROP_SUPPRESS_MS = 400;
let racePortalBackdropSuppressUntil = 0;

export function shouldSuppressRacePortalBackdropClose(): boolean {
  return typeof performance !== 'undefined' && performance.now() < racePortalBackdropSuppressUntil;
}

export function markRaceStackedOverlayJustClosed(): void {
  if (typeof performance === 'undefined') return;
  racePortalBackdropSuppressUntil = performance.now() + RACE_PORTAL_BACKDROP_SUPPRESS_MS;
}

export type RaceOverviewStatKey =
  | 'athletes'
  | 'athletePairs'
  | 'assignedDevices'
  | 'unretrieved'
  | 'retrieved';

/** Whether an overview KPI applies at the current race lifecycle stage. */
export function isRaceOverviewStatRelevant(stat: RaceOverviewStatKey, status: string): boolean {
  const st = normalizeRaceStatus(status);
  if (st === 'DELETED') return false;
  switch (stat) {
    case 'athletes':
      return true;
    case 'athletePairs':
    case 'assignedDevices':
      return st !== 'DRAFT';
    case 'unretrieved':
    case 'retrieved':
      return (
        st === 'DISTRIBUTION'
        || st === 'RACKING'
        || st === 'ACTIVE'
        || st === 'REPORTING'
        || st === 'COMPLETE'
      );
    default:
      return true;
  }
}

export function validationReportViewedStorageKey(eventId: string, raceId: string): string {
  return `fleet-validation-report-viewed:${String(eventId ?? '').trim()}:${String(raceId ?? '').trim()}`;
}

export function readValidationReportViewed(eventId: string, raceId: string): boolean {
  try {
    return sessionStorage.getItem(validationReportViewedStorageKey(eventId, raceId)) === '1';
  } catch {
    return false;
  }
}

export function writeValidationReportViewed(eventId: string, raceId: string): void {
  try {
    sessionStorage.setItem(validationReportViewedStorageKey(eventId, raceId), '1');
  } catch {
    /* sessionStorage unavailable */
  }
}

export function clearValidationReportViewed(eventId: string, raceId: string): void {
  try {
    sessionStorage.removeItem(validationReportViewedStorageKey(eventId, raceId));
  } catch {
    /* ignore */
  }
}

export type RaceValidationReviewOutcome = 'pass' | 'fail' | 'unknown';

/** Pass/fail from packaging readiness (`devicesMeetConfiguration` check). */
export function getRaceValidationReviewOutcome(input: {
  readinessEntry?: PackagingToDistributionReadinessCheck[] | { error: string };
  readinessLoading?: boolean;
}): RaceValidationReviewOutcome {
  if (input.readinessLoading) return 'unknown';
  const entry = input.readinessEntry;
  if (!entry || (typeof entry === 'object' && 'error' in entry)) return 'unknown';
  const check = entry.find((c) => c.key === 'devicesMeetConfiguration');
  if (!check) return 'unknown';
  return check.ok ? 'pass' : 'fail';
}

export function raceOverviewStatUnavailableHint(stat: RaceOverviewStatKey, status: string): string {
  const st = normalizeRaceStatus(status);
  if (st === 'DRAFT') {
    if (stat === 'athletePairs' || stat === 'assignedDevices') {
      return 'Device assignments start in Device Packaging.';
    }
    if (stat === 'unretrieved' || stat === 'retrieved') {
      return 'Retrieval tracking starts in Distribution.';
    }
  }
  if (st === 'PACKAGING' && (stat === 'unretrieved' || stat === 'retrieved')) {
    return 'Retrieval tracking starts in Distribution.';
  }
  if (st === 'DELETED') return 'Not available for deleted races.';
  return 'Not applicable at this stage.';
}

export type BuildRaceDetailActionGroupsInput = {
  eventId: string;
  race: EventRaceSummaryRow;
  raceId: string;
  status: string;
  next: string | null;
  prev: string | null;
  eventDeleted: boolean;
  isAdmin: boolean;
  canProgressRaces: boolean;
  progressing: boolean;
  canMoveForwardToPackaging: boolean;
  draftForwardTooltip?: string;
  hasAthletesRosterOnServer: boolean;
  canDevicePackaging: boolean;
  canShowValidationReport: boolean;
  canNavigateRaceDevices: boolean;
  canMarkStageRetrieved: boolean;
  canMoveForwardFromRacking?: boolean;
  rackingForwardDisabledReason?: string;
  canRecall: boolean;
  canDeleteRace: boolean;
  canHardDeleteRace: boolean;
  onAdvanceRace?: (eventId: string, race: EventRaceSummaryRow, nextStatus: string) => void;
  onOpenDevicePackaging?: (eventId: string, raceId: string) => void;
  onOpenRecall?: (eventId: string, raceId: string) => void;
  onNavigateToDevices?: (raceId?: string | null, options?: { unretrievedOnly?: boolean }) => void;
  onSoftDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  onHardDeleteRace?: (eventId: string, raceId: string, raceName?: string) => void;
  onOpenValidationReport?: () => void;
  onOpenStageRetrieval?: () => void;
  onClosePortal?: () => void;
};

export function buildRaceDetailActionGroups(input: BuildRaceDetailActionGroupsInput): RaceDetailActionGroup[] {
  const {
    eventId,
    race,
    raceId,
    status,
    next,
    prev,
    eventDeleted,
    progressing,
    canMoveForwardToPackaging,
    draftForwardTooltip,
    canDevicePackaging,
    canShowValidationReport,
    canNavigateRaceDevices,
    canMarkStageRetrieved,
    canMoveForwardFromRacking = true,
    rackingForwardDisabledReason,
    canRecall,
    canDeleteRace,
    canHardDeleteRace,
    onAdvanceRace,
    onOpenDevicePackaging,
    onOpenRecall,
    onNavigateToDevices,
    onSoftDeleteRace,
    onHardDeleteRace,
    onOpenValidationReport,
    onOpenStageRetrieval,
    onClosePortal,
  } = input;

  /** Only close the race portal when leaving this view (navigation/delete), not for stacked dialogs. */
  const closePortal = (): void => onClosePortal?.();
  const groups: RaceDetailActionGroup[] = [];

  const stageItems: RaceDetailActionGroup['items'] = [];
  const canNavigate = input.canProgressRaces && !eventDeleted && status !== 'DELETED' && !!onAdvanceRace;
  const showForward = canNavigate && !!next;
  const showBackward = canNavigate && !!prev;
  const isDraft = isDraftRaceStatus(status);

  if (showForward && isDraft) {
    stageItems.push({
      id: 'forward-packaging',
      label: progressing ? 'Updating…' : formatMoveToStatusLabel('forward', 'PACKAGING'),
      description: 'Move race to Device Packaging',
      disabled: progressing,
      title: draftForwardTooltip,
      tone: 'primary',
      onClick: () => {
        onAdvanceRace?.(eventId, race, 'PACKAGING');
      },
    });
  }
  if (showForward && !isDraft && next) {
    const disableForwardFromRacking = status === 'RACKING' && next === 'ACTIVE' && !canMoveForwardFromRacking;
    stageItems.push({
      id: 'forward-next',
      label: progressing ? 'Updating…' : formatMoveToStatusLabel('forward', next),
      tone: 'primary',
      disabled: progressing || disableForwardFromRacking,
      title: disableForwardFromRacking ? rackingForwardDisabledReason : undefined,
      onClick: () => {
        onAdvanceRace?.(eventId, race, next);
      },
    });
  }
  if (showBackward && prev) {
    stageItems.push({
      id: 'backward',
      label: progressing ? 'Updating…' : formatMoveToStatusLabel('backward', prev),
      disabled: progressing,
      onClick: () => {
        onAdvanceRace?.(eventId, race, prev);
      },
    });
  }
  if (stageItems.length) groups.push({ id: 'stage', title: 'Stage', items: stageItems });

  const setupItems: RaceDetailActionGroup['items'] = [];
  if (status === 'PACKAGING' && canDevicePackaging && onOpenDevicePackaging) {
    setupItems.push({
      id: 'packaging',
      label: 'Upload device assignments',
      tone: 'primary',
      onClick: () => {
        onOpenDevicePackaging(eventId, raceId);
      },
    });
  }
  if (setupItems.length) groups.push({ id: 'setup', title: 'Setup', items: setupItems });

  const checkItems: RaceDetailActionGroup['items'] = [];
  if (canShowValidationReport && onOpenValidationReport) {
    checkItems.push({
      id: 'validation',
      label: 'Validation report',
      onClick: () => {
        onOpenValidationReport();
      },
    });
  }
  if (checkItems.length) groups.push({ id: 'checks', title: 'Checks', items: checkItems });

  const retrievalItems: RaceDetailActionGroup['items'] = [];
  if (canMarkStageRetrieved && onOpenStageRetrieval) {
    retrievalItems.push({
      id: 'stage-retrieval',
      label: 'Edit retrieved devices',
      description: 'Scan or mark devices retrieved',
      tone: 'primary',
      onClick: () => {
        onOpenStageRetrieval();
      },
    });
  }
  if (status === 'ACTIVE' && canRecall && onOpenRecall) {
    retrievalItems.push({
      id: 'recall',
      label: 'Retrieve devices',
      onClick: () => {
        onOpenRecall(eventId, raceId);
      },
    });
  }
  if (retrievalItems.length) groups.push({ id: 'retrieval', title: 'Retrieval', items: retrievalItems });

  const navItems: RaceDetailActionGroup['items'] = [];
  if (canNavigateRaceDevices && onNavigateToDevices) {
    navItems.push({
      id: 'all-devices',
      label: 'View all devices',
      onClick: () => {
        onNavigateToDevices(raceId);
        closePortal();
      },
    });
    const st = normalizeRaceStatus(status);
    if (st === 'DISTRIBUTION' || st === 'RACKING' || st === 'ACTIVE') {
      navItems.push({
        id: 'unretrieved-devices',
        label: 'View unretrieved devices',
        onClick: () => {
          onNavigateToDevices(raceId, { unretrievedOnly: true });
          closePortal();
        },
      });
    }
  }
  if (navItems.length) groups.push({ id: 'navigation', title: 'Navigation', items: navItems });

  const dangerItems: RaceDetailActionGroup['items'] = [];
  if (canDeleteRace && onSoftDeleteRace) {
    dangerItems.push({
      id: 'soft-delete',
      label: 'Soft-delete race',
      tone: 'warn',
      onClick: () => {
        onSoftDeleteRace(eventId, raceId, race.name);
        closePortal();
      },
    });
  }
  if (canHardDeleteRace && onHardDeleteRace) {
    dangerItems.push({
      id: 'hard-delete',
      label: 'Hard-delete race',
      tone: 'danger',
      onClick: () => {
        onHardDeleteRace(eventId, raceId, race.name);
        closePortal();
      },
    });
  }
  if (dangerItems.length) groups.push({ id: 'danger', title: 'Deletion', items: dangerItems });

  return groups;
}

export type BuildRaceDetailPortalModelInput = {
  race: EventRaceSummaryRow;
  displayRows: EventDeviceRow[];
  fromAthleteCsvFallback: boolean;
  loadingCsvFallback: boolean;
  athletesCount: number | undefined;
  athletesCountLoaded: boolean;
  hasAthletesRoster: boolean;
  isDetailsLoaded: boolean;
  isRefreshingEventDetails: boolean;
  raceCfg: RaceConfigurationRequirements | null;
  raceCfgLoaded: boolean;
  raceCfgReady: boolean;
  readinessEntry?: PackagingToDistributionReadinessCheck[] | { error: string };
  readinessLoading: boolean;
  hasAnyRackedAthlete: boolean;
  rackingRackedDevices: number;
  rackingActiveDevices: number;
  isRackingComplete: boolean;
  eventDeleted: boolean;
  actionGroups: RaceDetailActionGroup[];
  renderStagedRetrieval?: () => import('react').ReactElement;
  draftForwardTooltip?: string;
  canMoveForwardToPackaging: boolean;
  progressing: boolean;
  validationReportViewed?: boolean;
};

export function buildRaceDetailPortalModel(input: BuildRaceDetailPortalModelInput): RaceDetailPortalModel {
  const rid = String(input.race.raceId ?? '').trim();
  const status = normalizeRaceStatus(input.race.status ?? 'DRAFT');
  const readOnly = input.eventDeleted || isRaceAssignmentsLocked(status);
  const lockedHint = input.eventDeleted
    ? 'This event is deleted and cannot be edited.'
    : readOnly
      ? 'This race is active, completed, or deleted — devices are read-only here.'
      : 'Device rows follow server rules for this race.';

  return {
    race: input.race,
    raceId: rid,
    status,
    displayRows: input.displayRows,
    fromAthleteCsvFallback: input.fromAthleteCsvFallback,
    loadingCsvFallback: input.loadingCsvFallback,
    athletesCount:
      input.hasAthletesRoster && input.athletesCountLoaded
        ? input.athletesCount ?? null
        : input.hasAthletesRoster
          ? null
          : 0,
    athletesCountLoading:
      !input.isDetailsLoaded
      || input.isRefreshingEventDetails
      || (input.hasAthletesRoster && !input.athletesCountLoaded),
    hasAthletesRoster: input.hasAthletesRoster,
    assignedDevices: countAssignedRaceDevices(input.displayRows),
    retrievedDevices: countRetrievedRaceDevices(input.displayRows),
    unretrievedDevices: countUnretrievedRaceDevices(input.displayRows, status),
    athleteDevicePairs: countRaceAthleteDevicePairs(input.displayRows),
    retrievedByPhase: countRetrievedRaceDevicesByPhase(input.displayRows),
    raceCfgReady: input.raceCfgReady,
    raceCfgLoaded: input.raceCfgLoaded,
    devicesPendingRetrievalLoading:
      countUnretrievedRaceDevices(input.displayRows, status) != null
      && (!input.isDetailsLoaded || input.isRefreshingEventDetails || input.loadingCsvFallback),
    readinessEntry: input.readinessEntry,
    readinessLoading: input.readinessLoading,
    hasAnyRackedAthlete: input.hasAnyRackedAthlete,
    rackingRackedDevices: input.rackingRackedDevices,
    rackingActiveDevices: input.rackingActiveDevices,
    isRackingComplete: input.isRackingComplete,
    groupReadOnly: readOnly,
    lockedHint,
    actionGroups: input.actionGroups,
    renderStagedRetrieval: input.renderStagedRetrieval,
    draftForwardTooltip: input.draftForwardTooltip,
    canMoveForwardToPackaging: input.canMoveForwardToPackaging,
    progressing: input.progressing,
    raceConfiguration: input.raceCfg,
    validationReportViewed: !!input.validationReportViewed,
  };
}
